/**
 * 예외 자동전달 규칙 추가·수정 팝업 (migration 162, 2026-09-05 사용자 지시).
 *
 * 보낸사람(메일주소 | 도메인) → 전달 메일 주소 목록.
 *
 * ★★★ **전달 주소는 «목록»이다**(사용자 지시) — 적고 「추가」를 누르면 아래 표에 쌓이고,
 *    골라서 지우거나 전부 지운다. 자동분류의 칩 입력(정본 B2-2)과 다른 자리다:
 *    거기는 **조건**이라 한눈에 훑는 것이 중요하고, 여기는 **보낼 곳**이라 하나씩 확인하며
 *    지우는 일이 잦다.
 * ★★ **주소가 하나도 없으면 등록할 수 없다** — 걸려도 아무 데도 안 가는 규칙이 된다.
 *    서버도 400 을 주지만 여기서 먼저 막아야 **왜 안 되는지가 팝업 안에** 보인다.
 * ★  도메인은 `@` 를 떼고 소문자로 담는다 — 사람은 「@corp.com」이라고 적는다.
 */

import { useId, useState } from 'react';
import { Button } from '../../components/shared/Button';
import { FormField } from '../../components/shared/FormField';
import { Input } from '../../components/shared/Input';
import { Modal, ModalActions } from '../../components/shared/Modal';
import { Select } from '../../components/shared/Select';
import { Table, type TableColumn } from '../../components/shared/Table';
import { tMail } from '../../lib/mail-i18n';
import type { MailForwardMatchKind, MailForwardRule, MailForwardRuleInput } from '../../api/mail';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DOMAIN_RE = /^[^\s@]+\.[^\s@]{2,}$/;
const TARGET_MAX = 20;

export interface MailForwardRuleModalProps {
  target?: MailForwardRule | null;
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (input: MailForwardRuleInput) => void;
}

export function MailForwardRuleModal({ target, busy, error, onClose, onSubmit }: MailForwardRuleModalProps) {
  const [kind, setKind] = useState<MailForwardMatchKind>(target?.matchKind ?? 'ADDRESS');
  const [value, setValue] = useState(target?.matchValue ?? '');
  const [targets, setTargets] = useState<string[]>(target?.addresses ?? []);
  const [draft, setDraft] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [local, setLocal] = useState<string | null>(null);
  const uid = useId();

  /** 한 줄 담기 — 「추가」와 Enter 가 같은 길을 쓴다. */
  const addTarget = () => {
    const a = draft.trim().toLowerCase();
    if (!EMAIL_RE.test(a)) { setLocal(tMail('mail.fwd.err_addr')); return; }
    if (targets.includes(a)) { setLocal(tMail('mail.fwd.err_exists')); return; }
    if (targets.length >= TARGET_MAX) return;
    setTargets([...targets, a]);
    setDraft('');
    setLocal(null);
  };

  const submit = () => {
    const v = kind === 'DOMAIN' ? value.trim().toLowerCase().replace(/^@+/, '') : value.trim().toLowerCase();
    if (kind === 'DOMAIN' ? !DOMAIN_RE.test(v) : !EMAIL_RE.test(v)) {
      setLocal(tMail(kind === 'DOMAIN' ? 'mail.fwd.err_domain' : 'mail.fwd.err_addr'));
      return;
    }
    if (targets.length === 0) { setLocal(tMail('mail.fwd.err_targets')); return; }
    setLocal(null);
    onSubmit({ matchKind: kind, matchValue: v, addresses: targets });
  };

  const columns: ReadonlyArray<TableColumn<{ address: string }>> = [
    { key: 'address', header: tMail('mail.fwd.col.address'), rowHeader: true, render: (r) => r.address },
  ];
  const rows = targets.map((address) => ({ address }));

  return (
    <Modal
      title={target ? tMail('mail.fwd.rule_edit_title') : tMail('mail.fwd.rule_add_title')}
      size="md"
      onClose={onClose}
      footer={(
        <ModalActions>
          <Button variant="secondary" onClick={onClose} disabled={busy}>{tMail('mail.fwd.cancel')}</Button>
          <Button variant="primary" onClick={submit} loading={busy}>
            {target ? tMail('mail.fwd.save') : tMail('mail.fwd.register')}
          </Button>
        </ModalActions>
      )}
    >
      <div className="mboxmodal">
        <FormField label={tMail('mail.fwd.f.sender')} required>
          <div className="filtrule__addr">
            <Select
              className="filtrule__kind"
              ariaLabel={tMail('mail.fwd.f.sender')}
              value={kind}
              onChange={(v) => { setKind(v as MailForwardMatchKind); setLocal(null); }}
              options={[
                { value: 'ADDRESS', label: tMail('mail.fwd.kind.address') },
                { value: 'DOMAIN', label: tMail('mail.fwd.kind.domain') },
              ]}
            />
            <Input
              /* ★ 한 줄에 컨트롤이 둘이라 FormField 의 id 는 **고르기**가 가져간다.
                 글자 칸은 제 id 와 이름을 따로 갖는다 — 안 그러면 id 가 겹치고
                 읽어 주는 프로그램이 이 칸의 이름을 모른다. */
              id={`${uid}-v`}
              aria-label={tMail('mail.fwd.f.sender')}
              value={value}
              maxLength={320}
              autoFocus
              placeholder={tMail(kind === 'DOMAIN' ? 'mail.fwd.f.sender_dom_ph' : 'mail.fwd.f.sender_addr_ph')}
              onChange={(e) => { setValue(e.target.value); if (local) setLocal(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              fullWidth
            />
          </div>
        </FormField>

        {/* 무엇이 일어나는지를 두 칸 사이에 적는다 — 팝업을 처음 여는 사람이 흐름을 읽는다. */}
        <p className="fwdrule__explain">{tMail('mail.fwd.f.explain')}</p>

        <FormField label={tMail('mail.fwd.f.targets')} required>
          <div className="fwdrule__add">
            <Input
              value={draft}
              maxLength={320}
              placeholder={tMail('mail.fwd.f.target_ph')}
              disabled={targets.length >= TARGET_MAX}
              aria-label={tMail('mail.fwd.f.targets')}
              onChange={(e) => { setDraft(e.target.value); if (local) setLocal(null); }}
              /* ★ 한 줄 칸의 Enter 는 여기서 막고 우리가 담는다 — 안 막으면 폼이 통째로 올라간다. */
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTarget(); } }}
              fullWidth
            />
            <Button variant="secondary" onClick={addTarget} disabled={targets.length >= TARGET_MAX}>
              {tMail('mail.fwd.add')}
            </Button>
          </div>

          <div className="fwdrule__acts">
            <span className="wc-set__hint">
              {tMail('mail.fwd.f.target_count').replace('{n}', String(targets.length))}
            </span>
            {/* 고른 것에 하는 일은 목록 «위»에 서고, 고른 것이 없으면 잠긴다. */}
            <Button
              variant="quiet-danger" size="sm"
              disabled={picked.length === 0}
              onClick={() => {
                setTargets(targets.filter((a) => !picked.includes(a)));
                setPicked([]);
              }}
            >
              {tMail('mail.fwd.select_delete')}
            </Button>
            <Button
              variant="quiet-danger" size="sm"
              disabled={targets.length === 0}
              onClick={() => { setTargets([]); setPicked([]); }}
            >
              {tMail('mail.fwd.clear')}
            </Button>
          </div>

          <Table
            caption={tMail('mail.fwd.f.targets')}
            columns={columns}
            rows={rows}
            rowKey={(r) => r.address}
            selectedKeys={picked}
            onSelectionChange={setPicked}
            rowSelectionLabel={(r) => r.address}
            empty={<span>{tMail('mail.fwd.f.target_none')}</span>}
            wrapClassName="mbox__tablewrap"
          />
        </FormField>

        {(local ?? error) && <p className="mboxmodal__err" role="alert">{local ?? error}</p>}
      </div>
    </Modal>
  );
}
